import { computed, onMounted, readonly, ref } from "vue";
import { getWorkspaceLocale } from "@/shared/infrastructure/adapters/workspaceLocaleService";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * The workspace reference currency, read from the BFF by each caller.
 *
 * CONSTRAINT: no module cache and no shared state. The currency is derived from the workspace
 * region today, but it is meant to become editable, and the BFF already caches the read.
 *
 * `immediate` (default) reads on mount; a modal that stays mounted while closed passes `false`
 * and calls `load()` when it opens.
 */
export function useWorkspaceCurrency(options = {}) {
    const currency = ref(null);
    const isLoading = ref(true);
    const loadError = ref(null);
    let latestRun = 0;
    async function load() {
        const run = ++latestRun;
        isLoading.value = true;
        try {
            const workspaceLocale = await getWorkspaceLocale();
            if (run !== latestRun)
                return;
            currency.value = workspaceLocale?.currency ?? null;
            loadError.value = null;
        }
        catch (error) {
            if (run !== latestRun)
                return;
            currency.value = null;
            loadError.value = error;
            logger.error("useWorkspaceCurrency: workspace locale read failed", error);
        }
        finally {
            if (run === latestRun)
                isLoading.value = false;
        }
    }
    if (options.immediate ?? true)
        onMounted(load);
    return {
        currency: readonly(currency),
        isLoading: readonly(isLoading),
        loadError: readonly(loadError),
        isUnavailable: computed(() => !isLoading.value && currency.value === null),
        load,
    };
}
